import { I18n } from '@affine/i18n';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { css, html, nothing } from 'lit';
import { property } from 'lit/decorators.js';

import type { CanonicalMessageContextItem } from '../../components/ai-chat-chips/canonical-message-context';
import {
  automationAttachmentsFromMessage,
  codeSelectionsFromMessage,
  databaseAttachmentsFromMessage,
  genericContextItemsFromMessage,
  mailAttachmentsFromMessage,
  pdfContextAttachmentsFromMessage,
  selectedContextChipsFromMessage,
  workflowAttachmentsFromMessage,
} from '../../components/ai-chat-chips/message-context';
import type { SelectedContextChip } from '../../components/ai-chat-chips/type';
import type {
  AutomationAttachment,
  CanvasAIChatContextMode,
  CodeSelectionAttachment,
  DatabaseAttachment,
  PdfContextAttachment,
  WorkflowAttachment,
} from '../../components/ai-chat-content/type';
import { type ChatMessage } from '../../components/ai-chat-messages';
import {
  aiChatBodyTypography,
  aiChatStatusTypography,
} from '../../styles/ai-chat-typography';
import { sanitizeChatMessageContentForDisplay } from '../../utils/blob-url-resolver';
import type { MailMessageAttachment } from '../../utils/mail-selection-context';

function renderReadonlyContextChips(
  databaseAttachments: DatabaseAttachment[],
  automationAttachments: AutomationAttachment[],
  workflowAttachments: WorkflowAttachment[],
  codeSelections: CodeSelectionAttachment[],
  selectedContextChips: SelectedContextChip[],
  mailAttachments: MailMessageAttachment[],
  pdfContextAttachments: PdfContextAttachment[],
  genericContextItems: CanonicalMessageContextItem[]
) {
  const contextCount =
    genericContextItems.length +
    databaseAttachments.length +
    automationAttachments.length +
    workflowAttachments.length +
    codeSelections.length +
    selectedContextChips.length +
    mailAttachments.length +
    pdfContextAttachments.length;
  if (contextCount === 0) return nothing;
  return html`<div class="message-context-wrapper">
    <chat-panel-context-group-chip
      .databaseAttachments=${databaseAttachments}
      .automationAttachments=${automationAttachments}
      .workflowAttachments=${workflowAttachments}
      .codeSelections=${codeSelections}
      .selectedContextChips=${selectedContextChips}
      .mailAttachments=${mailAttachments}
      .pdfContextAttachments=${pdfContextAttachments}
      .genericContextItems=${genericContextItems}
      .readOnly=${true}
      .inlineExpandable=${true}
    ></chat-panel-context-group-chip>
  </div>`;
}

export class ChatMessageUser extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    chat-message-user {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    .chat-message-user {
      ${aiChatBodyTypography}
      display: flex;
      flex-direction: column;
      max-width: calc(100% - 58px);
    }

    .chat-content-images {
      display: flex;
      justify-content: flex-end;

      .images-row {
        margin-left: auto;
      }
    }

    .text-content-wrapper {
      align-self: flex-end;
    }

    .message-context-wrapper {
      --chat-message-context-gap: 4px;
      --streamlain-disclosure-overlay-gap: var(--chat-message-context-gap);

      align-self: flex-end;
      margin-top: var(--chat-message-context-gap);
      margin-bottom: 6px;
      max-width: 100%;
    }

    .approval-response {
      ${aiChatStatusTypography}
      align-self: flex-end;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      background: var(--streamlain-background-secondary-color);
      color: var(--streamlain-text-primary-color);
      padding: 8px 10px;
      max-width: 100%;
      box-sizing: border-box;
    }

    .approval-response[data-approved='true'] {
      border-color: var(--streamlain-processing-color);
    }

    .approval-response[data-approved='false'] {
      opacity: 0.78;
    }

    .approval-response[data-mode-choice='true'] {
      border-color: var(--streamlain-processing-color);
      opacity: 1;
    }

    .approval-response-reason {
      color: var(--streamlain-text-secondary-color);
      margin-top: 2px;
      overflow-wrap: anywhere;
    }
  `;

  @property({ attribute: false })
  accessor item!: ChatMessage;

  @property({ attribute: false })
  accessor canvasAIChatContextMode: CanvasAIChatContextMode = 'lineage-only';

  @property({ attribute: 'data-testid', reflect: true })
  accessor testId = 'chat-message-user';

  private renderApprovalResponse() {
    const toolData = this.item.toolData;
    const approved =
      toolData && typeof toolData.approved === 'boolean'
        ? toolData.approved
        : this.item.content.toLowerCase().startsWith('approved:');
    const reason =
      toolData && typeof toolData.reason === 'string' ? toolData.reason : null;
    const isModeChoice =
      typeof toolData?.approvalId === 'string' &&
      /^(?:agent|target)-mode-proposal:/.test(toolData.approvalId);
    const includesAgentMode = toolData?.includesAgentMode === true;
    const includesTargetMode = toolData?.includesTargetMode === true;
    const modeChoiceLabel = approved
      ? includesAgentMode && includesTargetMode
        ? I18n.t(
            'com.streamlain.ai.chat.input.target.proposal.history.agent-target'
          )
        : includesAgentMode
          ? I18n.t('com.streamlain.ai.chat.input.target.proposal.history.agent')
          : I18n.t(
              'com.streamlain.ai.chat.input.target.proposal.history.with-target'
            )
      : includesAgentMode
        ? I18n.t('com.streamlain.ai.chat.input.target.proposal.history.chat')
        : I18n.t(
            'com.streamlain.ai.chat.input.target.proposal.history.direct-agent'
          );
    return html`<div
      class="approval-response"
      data-approved=${approved ? 'true' : 'false'}
      data-mode-choice=${isModeChoice ? 'true' : 'false'}
      data-testid="chat-approval-response"
    >
      ${isModeChoice
        ? modeChoiceLabel
        : approved
          ? I18n.t('com.streamlain.ai.confirmation.history.approved')
          : I18n.t('com.streamlain.ai.confirmation.history.rejected')}
      ${reason
        ? html`<div class="approval-response-reason">${reason}</div>`
        : nothing}
    </div>`;
  }

  renderContent() {
    const { item } = this;
    if (item.messageType === 'tool_approval_response') {
      return this.renderApprovalResponse();
    }

    const databaseAttachments = databaseAttachmentsFromMessage(item);
    const automationAttachments = automationAttachmentsFromMessage(item);
    const workflowAttachments = workflowAttachmentsFromMessage(item);
    const codeSelections = codeSelectionsFromMessage(item);
    const selectedContextChips = selectedContextChipsFromMessage(
      item,
      this.canvasAIChatContextMode
    );
    const mailAttachments = mailAttachmentsFromMessage(item);
    const pdfContextAttachments = pdfContextAttachmentsFromMessage(item);
    const genericContextItems = genericContextItemsFromMessage(item);
    const content = sanitizeChatMessageContentForDisplay(item.content);

    return html`
      ${item.attachments
        ? html`<chat-content-images
            class="chat-content-images"
            .images=${item.attachments}
          ></chat-content-images>`
        : nothing}
      <div
        class="text-content-wrapper"
        data-test-id="chat-content-user-text"
        style="max-width: 100%;"
      >
        <chat-content-pure-text .text=${content}></chat-content-pure-text>
      </div>
      ${renderReadonlyContextChips(
        databaseAttachments,
        automationAttachments,
        workflowAttachments,
        codeSelections,
        selectedContextChips,
        mailAttachments,
        pdfContextAttachments,
        genericContextItems
      )}
    `;
  }

  protected override render() {
    return html` <div class="chat-message-user">${this.renderContent()}</div> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chat-message-user': ChatMessageUser;
  }
}
